import React from 'react'
import PropTypes from 'prop-types'
import Notice from '../shared/Notice'
import {OWNERS} from '../../constants'
import {formatEfficiency, formatInt, formatPercent} from '../../helpers/format'

/**
 * Bốn thông báo mức dữ liệu của báo cáo M18 (FR-008): lỗi đọc giờ làm, độ phủ định mức thấp, mốc
 * dữ liệu, định mức lệch.
 *
 * ⚠️ Mốc "số liệu đủ tới đâu" CỐ Ý không ở đây mà ở phụ đề trang, cạnh `computedAt` — xem docblock
 *    tại chỗ trong `OutputReportPage`. Tóm tắt: độ trễ đó luôn ≤ 2 × chu kỳ làm tươi (mặc định 10
 *    phút) vì cổng `read()` của service không phục vụ ô cũ hơn thế, nên dựng một hộp cảnh báo cho
 *    nó là dạy người dùng bỏ qua hộp cảnh báo. Và nó phải đứng CẠNH `computedAt` thì chênh lệch
 *    giữa hai con số mới tự giải thích được.
 *
 * ⚠️ Tách ra component riêng KHÔNG phải để gọn mắt. `OutputReportPage` đã ~1.400 dòng, và
 *    `eslint-plugin-react-hooks` bản repo đang dùng đếm số nhánh trên TOÀN hàm: mỗi `{cond && ()}`
 *    thêm vào JSX của nó đẩy bộ đếm lên, và khi bộ đếm tràn thì plugin báo NHẦM "useMemo is called
 *    conditionally" cho những hook ở tít phía trên, không liên quan gì tới chỗ vừa sửa. Đã mất một
 *    lượt truy nhầm vì chuyện này. Bốn nhánh ở đây là bốn nhánh KHÔNG còn nằm trong hàm đó.
 *
 * ⚠️ Thứ tự hiển thị có ý nghĩa, từ nặng tới nhẹ, và JSX dưới đây phải ĐI ĐÚNG thứ tự đó:
 *      1. không đọc được dữ liệu (đỏ) — không có số nào để dùng;
 *      2. số đang là ƯỚC TÍNH, không dùng tính lương (vàng) — điều kiện PO ra để được ra mắt;
 *      3. thước đo có thể sai (đỏ) — số có nhưng đừng tin;
 *      4. một phần kỳ không có dữ liệu (xanh) — số đúng, chỉ thiếu đầu kỳ.
 *
 *    Bản trước xếp 1 → 4 → 2 → 3, tức đẩy câu "không dùng tính thưởng/lương" xuống DƯỚI một Notice
 *    màu xanh chỉ để thông tin. Người đọc dừng ở thông báo đầu tiên họ thấy.
 */
function ReportNotices({
    hoursUnavailable,
    lowCoverageStages,
    coverageThreshold,
    milestoneNotes,
    rateWarnings,
    onOpenStandardTime,
}) {
    return (
        <>
        {/*
          * EC-I-04 — KHÔNG đọc được collection giờ làm.
          *
          * ⚠️ Phải nói rõ là LỖI ĐỌC. Khi đó mọi cột hiệu suất đều trống, y hệt lúc chưa ai
          *    nhập giờ — mà hai nguyên nhân đó dẫn tới hai việc khác hẳn nhau: một bên là gọi
          *    IT, một bên là đi nhập chấm công. Không có dòng này thì cả xưởng đi nhập lại giờ
          *    cho dữ liệu đã có đủ.
          */}
        {hoursUnavailable && (
            <Notice level="error">
                <div>
                    <strong>Không đọc được dữ liệu giờ làm</strong> — các cột sản lượng bên dưới vẫn đầy
                    đủ và vẫn đúng, nhưng sáu cột hiệu suất không tính được.
                </div>
                <div className="VcHint">
                    Đây là lỗi đọc dữ liệu, KHÔNG phải do chưa ai nhập giờ. Thử tải lại; còn lỗi thì báo
                    bộ phận kỹ thuật, đừng nhập lại chấm công.
                </div>
            </Notice>
        )}

        {/*
          * BR-8.3 / PO câu 25 — điều kiện PO đặt ra để được ra mắt ngay.
          *
          * ⚠️ Dòng "không dùng tính thưởng/lương" là phần KHÔNG được rút gọn. Hiệu suất của một
          *    công đoạn mới phủ 40% định mức trông y hệt hiệu suất của công đoạn phủ 100% —
          *    cùng một con số, cùng một màu, cùng một hạng — và con số đó đi thẳng vào bảng
          *    lương nếu không có ai nói ra.
          */}
        {lowCoverageStages.length > 0 && (
            <Notice level="warning">
                <div>
                    <strong>
                        {formatInt(lowCoverageStages.length)} công đoạn có độ phủ định mức dưới{' '}
                        {formatPercent(coverageThreshold)}
                    </strong>
                    {' — '}
                    {lowCoverageStages
                        .map((stage) => `${stage.name} ${formatEfficiency(stage.coverage)}`)
                        .join(' · ')}
                </div>
                <div className="VcHint">
                    Hiệu suất ở những công đoạn này phần lớn là <strong>ước tính</strong>: phần sản lượng
                    chưa có định mức không vào được tử số, nên con số thấp hơn thực tế.{' '}
                    <strong>Không dùng để tính thưởng hay lương</strong> cho tới khi độ phủ đạt{' '}
                    {formatPercent(coverageThreshold)}. Người xử lý: <strong>{OWNERS.standard}</strong>.{' '}
                    <button
                        type="button"
                        className="VcClickableNumber"
                        onClick={onOpenStandardTime}
                    >
                        Mở Chỉ số tiêu chuẩn →
                    </button>
                </div>
            </Notice>
        )}

        {/*
          * Định mức lệch: trung vị cả công đoạn ra ngoài khoảng hợp lý thì gần như chắc chắn sai ở
          * ĐỊNH MỨC, không phải ở người — không công đoạn nào mà mọi người đều nhanh gấp rưỡi. Nói
          * ra TRƯỚC khi ai đó dùng con số này để đánh giá, kèm đường đi tới chỗ sửa.
          */}
        {rateWarnings.length > 0 && (
            /*
             * ⚠️ Mức ĐỎ, không phải vàng (FR-008). Vàng ở đây đứng cạnh ba thông báo vàng khác
             *    và chìm nghỉm, trong khi đây là thông báo duy nhất nói rằng THƯỚC ĐO sai —
             *    mọi con số của công đoạn đó, kể cả hạng, đều không dùng được cho tới khi sửa.
             */
            <Notice level="error">
                <div>
                    <strong>
                        {formatInt(rateWarnings.length)} công đoạn có trung vị hiệu suất lệch bất thường
                    </strong>
                    {' — '}
                    {rateWarnings
                        .map((w) => `${w.name} ${formatEfficiency(w.median)}`)
                        .join(' · ')}
                </div>
                <div className="VcHint">
                    Cả công đoạn cùng lệch một chiều thường là định mức đặt sai, không phải người làm
                    nhanh hay chậm. Chấm điểm người theo một thước đo sai là hỏng niềm tin vào cả báo
                    cáo. {/* BR-8.2: mọi thông báo phải ghi rõ AI xử lý, nếu không nó là một câu than. */}
                    Người xử lý: <strong>{OWNERS.standard}</strong>.{' '}
                    {/*
                      * `<button>` theo đúng khuôn `VcClickableNumber` đang có, không dùng
                      * `<span role="button">` tự chế: nút thật đã có sẵn tiêu điểm bàn phím,
                      * Enter/Space và vai trò đúng cho trình đọc màn hình.
                      */}
                    <button
                        type="button"
                        className="VcClickableNumber"
                        onClick={onOpenStandardTime}
                    >
                        Mở Chỉ số tiêu chuẩn →
                    </button>
                </div>
            </Notice>
        )}
        {/*
          * EC-B-05 — công đoạn chỉ quy được sản lượng về người TỪ một mốc, mà khoảng lọc lại bắt
          * đầu trước mốc đó.
          *
          * ⚠️ Mức THÔNG TIN, không phải cảnh báo: số sau mốc vẫn đúng, chỉ phần đầu kỳ trống.
          *    Nhưng phải nói ra, nếu không ô "—" của Hoàn thiện / Đóng gói bị báo là lỗi dữ liệu —
          *    đúng loại báo lỗi cho một hệ thống đang chạy bình thường.
          */}
        {milestoneNotes.length > 0 && (
            <Notice level="info">
                <div>
                    <strong>Mốc dữ liệu trong khoảng đang lọc</strong> — một phần đầu kỳ không quy được sản
                    lượng về người:
                </div>
                <ul className="VcReadingList">
                    {milestoneNotes.map((note) => (
                        <li key={note.stageCode}>
                            <strong>{note.name}</strong>: {note.blockerReason}
                        </li>
                    ))}
                </ul>
            </Notice>
        )}

        </>
    )
}

ReportNotices.propTypes = {
    /** EC-I-04 — lỗi ĐỌC collection giờ làm, khác hẳn "chưa ai nhập giờ". */
    hoursUnavailable: PropTypes.bool,
    lowCoverageStages: PropTypes.array,
    /** Ngưỡng độ phủ lấy từ service — PO chỉnh được, đừng hardcode 90% ở màn hình. */
    coverageThreshold: PropTypes.number,
    milestoneNotes: PropTypes.array,
    rateWarnings: PropTypes.array,
    onOpenStandardTime: PropTypes.func.isRequired,
}

ReportNotices.defaultProps = {
    hoursUnavailable: false,
    lowCoverageStages: [],
    milestoneNotes: [],
    rateWarnings: [],
}

export default ReportNotices
